import { useState, useEffect } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'

import { AlertWithActionsType } from '@masfront/common-b2c'

import { createOrder, createCheckoutItem } from '../../store/checkout.reducer'
import {
  selectContractId,
  selectMainLineError,
  selectAllLinesInfoAndRequests,
  selectAllLinesInfoAndStatus,
  selectMainLineTariffIsSelected,
} from '../../store/checkout.selectors'
import { CheckoutItemProps, lineDescription } from '../CheckoutItem'
import { ItemStatusProps } from '../ItemStatus'

import { selectAllLinesIds } from '~/line/store/line.selectors'
import { CHECKOUT_MAINLINE_ERROR, CHECKOUT_ERROR } from '~/modal/constants/messages'
import useModal from '~/modal/hooks/useModal'
import ModalMessages from '~/modal/models/modal.model'
import { LineTypes } from '~/tariff/model/tariff.model'

interface UseCheckoutListReturn {
  checkoutItems: CheckoutItemProps[] | undefined
  shouldCheckoutStart: boolean
}

const checkoutItemTypes = [
  {
    type: AlertWithActionsType.info,
    text: 'A la espera de realizar el pedido...',
    actions: [],
    tooltipMessage: '',
  },
  {
    type: AlertWithActionsType.loading,
    text: 'Creando el pedido',
    actions: [],
    tooltipMessage: 'Se están comprobando los datos de la venta y generando un número de pedido.',
  },
  {
    type: AlertWithActionsType.success,
    text: 'Pedido Nº ',
    actions: [],
    tooltipMessage: '',
  },
  {
    type: AlertWithActionsType.warning,
    text: 'Error al crear el pedido',
    actions: [
      {
        name: 'Reintentar',
        fn: () => null,
      },
    ],
    tooltipMessage: 'Ha habido un problema al crear el pedido',
  },
  {
    type: AlertWithActionsType.error,
    text: 'Imposible crear el pedido',
    actions: [
      {
        name: '¿Qué hacer ahora?',
        fn: () => null,
      },
    ],
    tooltipMessage: '',
  },
]

export default function useCheckoutList(): UseCheckoutListReturn {
  const dispatch = useDispatch()
  const allLinesIds = useSelector(selectAllLinesIds)
  const requests = useSelector(selectAllLinesInfoAndRequests)
  const mainLineError = useSelector(selectMainLineError, shallowEqual)
  const mainLineTariffIsSelected = useSelector(selectMainLineTariffIsSelected)
  const contractId = useSelector(selectContractId)
  const allLinesStatus = useSelector(selectAllLinesInfoAndStatus)
  const { selectAndOpenModalFromList } = useModal()
  const [checkoutItems, setCheckoutItems] = useState<CheckoutItemProps[]>()

  const checkoutModalMessages: ModalMessages = {
    CHECKOUT_ERROR_MAINLINE: {
      open: false,
      title: CHECKOUT_MAINLINE_ERROR.title,
      description: CHECKOUT_MAINLINE_ERROR.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CHECKOUT_MAINLINE_ERROR.actions.retry,
          fn: () => dispatchCreateOrder(LineTypes.MAIN),
        },
      ],
      type: 'error',
    },
    CHECKOUT_ERROR: {
      open: false,
      title: CHECKOUT_ERROR.title,
      description: `Código de error: ${mainLineError?.error?.code}. Mensaje: ${mainLineError?.error?.message}`,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CHECKOUT_ERROR.actions.exit,
          fn: () => null,
        },
      ],
      type: 'error',
    },
  }

  function createCheckoutItems(
    allLinesInfoAndStatus: {
      line: lineDescription
      id: string
      status: AlertWithActionsType
      orderNumber: number | undefined
    }[],
  ) {
    return allLinesInfoAndStatus.map(({ id, line, status, orderNumber }) => {
      return {
        line: { ...line },
        status: {
          ...checkoutItemTypes.find((item) => item.type === status),
          ...(getConfigurationFromStatus(id, status, orderNumber) as ItemStatusProps),
        },
        dataHook: `checkoutItem-${id}`,
      }
    })
  }

  function getConfigurationFromStatus(
    id: string,
    status: AlertWithActionsType,
    orderNumber: number | undefined,
  ) {
    let statusConfiguration
    switch (status) {
      case AlertWithActionsType.success:
        statusConfiguration = {
          text: orderNumber ? `Pedido Nº ${orderNumber}` : 'Pedido finalizado',
        }
        break
      case AlertWithActionsType.warning:
        statusConfiguration = {
          actions: [
            {
              name: 'Reintentar',
              fn: () => dispatchCreateOrder(id),
            },
          ],
        }
        break
      case AlertWithActionsType.error:
        statusConfiguration = {
          actions: [
            {
              name: '¿Qué hacer ahora?',
              fn: () => selectAndOpenModalFromList('CHECKOUT_ERROR', checkoutModalMessages),
            },
          ],
        }
        break
      default:
        break
    }
    return { ...statusConfiguration }
  }

  useEffect(() => {
    allLinesIds.forEach((lineId) => {
      dispatch(createCheckoutItem({ id: lineId }))
    })
  }, [allLinesIds])

  useEffect(() => {
    if (allLinesStatus.length === allLinesIds.length) {
      const allCheckoutItems = createCheckoutItems(allLinesStatus)
      setCheckoutItems(allCheckoutItems)
    }
  }, [allLinesStatus, allLinesIds])

  const dispatchCreateOrder = (lineId: string) =>
    dispatch(createOrder({ saleRequest: requests[lineId].saleRequest, id: lineId }))

  useEffect(() => {
    if (!contractId && mainLineTariffIsSelected) dispatchCreateOrder(LineTypes.MAIN)
  }, [contractId, mainLineTariffIsSelected])

  useEffect(() => {
    if (
      mainLineTariffIsSelected &&
      mainLineError &&
      (!mainLineError.httpStatus || mainLineError.error?.code)
    ) {
      selectAndOpenModalFromList('CHECKOUT_ERROR_MAINLINE', checkoutModalMessages)
    }
  }, [mainLineError])

  const createAllOrders = () => {
    const { mainLine, ...additionalLines } = requests

    const allAdditionalLines = Object.keys(additionalLines)

    if (allAdditionalLines.length > 0)
      allAdditionalLines.forEach((lineId) => {
        dispatchCreateOrder(lineId)
      })
  }

  useEffect(() => {
    if (contractId) createAllOrders()
  }, [contractId])

  return {
    checkoutItems,
    shouldCheckoutStart: !!mainLineTariffIsSelected,
  }
}
